/**
 * OmegaActivityPage — `/omega/activity`.
 *
 * Phase 14 live activity dashboard. Three sections:
 *
 *   1. OMEGA Vital Signs   — heartbeat indicator + 6 brain tiles
 *   2. Live Log Stream     — auto-scroll SSE tail, color-coded entry cards
 *   3. Recent Decisions    — pending items from /omega/decisions/pending
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Brain, ChevronRight, Pause, Play, X } from 'lucide-react';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { humanizeDecision } from '@/features/omega/decisionHumanizer';
import { useBrainSummaries, useOmegaState } from '@/features/omega/dashboard';
import { cn } from '@/utils/cn';
import { api } from '@/api/client';
import { apiFetch } from '@/api/http';
import type { BrainSummary, PendingDecision } from '@/features/omega/dashboard/data/types';
import type { BrainStatus, OmegaLogEntry } from '@/api/types';

// ─── Constants ────────────────────────────────────────────────────

const MAX_ENTRIES = 1_000;
const POLL_INTERVAL_MS = 2_000;
const HEARTBEAT_FADE_MS = 800;

// ─── Color coding ────────────────────────────────────────────────

export type EntryColor = 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'teal' | 'gray';

export function getEntryColor(entryType: string): EntryColor {
  if (
    entryType === 'omega_genesis' ||
    entryType === 'constitution_ratification' ||
    entryType === 'constitution_amendment' ||
    entryType === 'foreseeability_envelope' ||
    entryType === 'founder_key_fingerprint' ||
    entryType === 'brain_binding_updated'
  ) {
    return 'purple';
  }
  if (
    entryType === 'omega_kill' ||
    entryType === 'omega_founder_kill'
  ) {
    return 'red';
  }
  if (
    entryType === 'decision_pending' ||
    entryType === 'decision_acted'
  ) {
    return 'green';
  }
  if (
    entryType === 'entity' ||
    entryType === 'attestation' ||
    entryType === 'brain_execution'
  ) {
    return 'blue';
  }
  if (entryType.startsWith('trigger_')) {
    return 'orange';
  }
  if (entryType === 'consent' || entryType === 'revocation') {
    return 'teal';
  }
  return 'gray';
}

/** Extract the best available timestamp from an entry's payload. */
function extractTimestamp(entry: OmegaLogEntry): string | null {
  if (!entry.payload_json || typeof entry.payload_json !== 'object') return null;
  const p = entry.payload_json as Record<string, unknown>;
  const ts =
    p.fired_at ?? p.created_at ?? p.signed_at ?? p.issued_at ??
    p.acted_at ?? p.granted_at ?? p.revoked_at ?? p.timestamp;
  return typeof ts === 'string' ? ts : null;
}

/** Short human-readable time relative to now. */
function relTime(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  return `${Math.round(diff / 3_600_000)}h ago`;
}

// ─── useOmegaLogStream hook ────────────────────────────────────────

interface LogStreamState {
  entries: OmegaLogEntry[];
  nextSeq: number;
  total: number;
  paused: boolean;
  sseConnected: boolean;
}

function useOmegaLogStream() {
  const [state, setState] = useState<LogStreamState>({
    entries: [],
    nextSeq: 0,
    total: 0,
    paused: false,
    sseConnected: false,
  });
  const closeRef = useRef<(() => void) | null>(null);
  const errorCountRef = useRef(0);
  const nextSeqRef = useRef(0);
  const pausedRef = useRef(false);

  const addEntries = useCallback((incoming: OmegaLogEntry[]) => {
    if (pausedRef.current) return;
    setState((prev) => {
      const merged = [...prev.entries, ...incoming];
      const capped = merged.length > MAX_ENTRIES
        ? merged.slice(merged.length - MAX_ENTRIES)
        : merged;
      const newNextSeq = incoming[incoming.length - 1]!.seq + 1;
      nextSeqRef.current = newNextSeq;
      return { ...prev, entries: capped, nextSeq: newNextSeq };
    });
  }, []);

  // Initial load
  useEffect(() => {
    api.getOmegaLogTail(undefined, 50).then((resp) => {
      setState((prev) => ({
        ...prev,
        entries: resp.entries,
        nextSeq: resp.next_seq,
        total: resp.total,
      }));
      nextSeqRef.current = resp.next_seq;
    }).catch(() => {});
  }, []);

  // SSE or polling
  useEffect(() => {
    // Wait for initial load
    const setupStream = () => {
      if (api.connectOmegaLogStream) {
        // Real mode: use SSE
        const close = api.connectOmegaLogStream({
          sinceSeq: nextSeqRef.current,
          onEntries: (entries) => {
            errorCountRef.current = 0;
            addEntries(entries);
          },
          onError: () => {
            errorCountRef.current += 1;
            if (errorCountRef.current > 3) {
              // Too many errors — fall back to polling
              close();
              closeRef.current = null;
              setState((prev) => ({ ...prev, sseConnected: false }));
              startPolling();
            }
          },
        });
        closeRef.current = close;
        setState((prev) => ({ ...prev, sseConnected: true }));
      } else {
        startPolling();
      }
    };

    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      pollTimer = setInterval(async () => {
        if (pausedRef.current) return;
        try {
          const resp = await api.getOmegaLogTail(nextSeqRef.current, 50);
          if (resp.entries.length > 0) addEntries(resp.entries);
          setState((prev) => ({ ...prev, total: resp.total }));
        } catch {
          // network blip — retry next tick
        }
      }, POLL_INTERVAL_MS);
    };

    // Small delay to let the initial load settle
    const initTimer = setTimeout(setupStream, 500);

    return () => {
      clearTimeout(initTimer);
      if (pollTimer) clearInterval(pollTimer);
      closeRef.current?.();
    };
  }, [addEntries]);

  const setPaused = useCallback((paused: boolean) => {
    pausedRef.current = paused;
    setState((prev) => ({ ...prev, paused }));
  }, []);

  return { ...state, setPaused };
}

// ─── Page ────────────────────────────────────────────────────────

export function OmegaActivityPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  const [drawerEntry, setDrawerEntry] = useState<OmegaLogEntry | null>(null);
  const logStream = useOmegaLogStream();

  return (
    <div className="flex flex-col gap-6 pb-8">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link to="/omega" className="text-xs text-ink-subtle hover:text-ink">OMEGA</Link>
          <ChevronRight size={12} className="text-ink-muted" aria-hidden />
          <span className="text-xs font-medium text-ink">Activity</span>
        </div>
        <h1 className="sr-only">OMEGA Activity</h1>
      </header>

      {/* ── 1. Vital Signs ── */}
      <VitalSignsSection totalLogEntries={logStream.total} />

      {/* ── 1b. OMEGA Brain Loop ── */}
      <BrainLoopCard />

      {/* ── 2+3. Log + Decisions ── */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <LogStreamSection
            entries={logStream.entries}
            total={logStream.total}
            paused={logStream.paused}
            sseConnected={logStream.sseConnected}
            onPauseToggle={logStream.setPaused}
            onEntryClick={setDrawerEntry}
          />
        </div>
        <div className="lg:w-80 lg:shrink-0">
          <DecisionsSidebar />
        </div>
      </div>

      {/* ── Entry drawer ── */}
      {drawerEntry && (
        <EntryDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />
      )}
    </div>
  );
}

// ─── VitalSignsSection ────────────────────────────────────────────

function VitalSignsSection({ totalLogEntries }: { totalLogEntries: number }) {
  const stateQuery = useOmegaState();
  const brainsQuery = useBrainSummaries();
  const [heartbeat, setHeartbeat] = useState(false);
  const prevTotalRef = useRef(totalLogEntries);

  useEffect(() => {
    if (totalLogEntries > prevTotalRef.current) {
      setHeartbeat(true);
      const t = setTimeout(() => setHeartbeat(false), HEARTBEAT_FADE_MS);
      prevTotalRef.current = totalLogEntries;
      return () => clearTimeout(t);
    }
    prevTotalRef.current = totalLogEntries;
  }, [totalLogEntries]);

  const brains = brainsQuery.data ?? [];

  return (
    <section aria-label="OMEGA vital signs" className="rounded-xl border border-border bg-surface p-5">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          OMEGA Vital Signs
        </h2>
        {/* Heartbeat */}
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'inline-block h-2.5 w-2.5 rounded-full transition-all duration-150',
              heartbeat
                ? 'scale-150 bg-success shadow-[0_0_6px_2px_var(--color-success)]'
                : 'bg-success/40',
            )}
            aria-label={heartbeat ? 'New log entry' : 'Idle'}
          />
          <span className="font-mono text-[10px] text-ink-subtle">
            {totalLogEntries.toLocaleString()} entries
          </span>
        </div>
        {stateQuery.data && (
          <span className={cn(
            'ms-auto rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
            stateQuery.data.alive
              ? 'border-success/30 bg-success-soft text-success'
              : 'border-danger/30 bg-danger/10 text-danger',
          )}>
            {stateQuery.data.alive ? 'ALIVE' : 'KILLED'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {brains.map((brain) => (
          <BrainVitalTile key={brain.id} brain={brain} />
        ))}
      </div>
    </section>
  );
}

function BrainVitalTile({ brain }: { brain: BrainSummary }) {
  const statusColor = {
    healthy: 'bg-success',
    live: 'bg-success',
    degraded: 'bg-warning',
    critical: 'bg-danger',
    paused: 'bg-ink-subtle',
    offline: 'bg-ink-subtle',
    blueprint: 'bg-sky-400',
  }[brain.status] ?? 'bg-ink-subtle';

  return (
    <Link
      to={`/omega/brain/${brain.id}`}
      className="group flex flex-col items-center gap-1.5 rounded-lg border border-border bg-canvas p-3 transition-colors hover:border-spark/40 hover:bg-spark/5"
      data-testid={`brain-tile-${brain.id}`}
    >
      <div className="relative">
        <Brain size={18} className="text-ink-muted group-hover:text-spark" aria-hidden />
        <span
          className={cn(
            'absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full border border-canvas',
            statusColor,
            brain.status === 'live' && 'animate-pulse',
          )}
        />
      </div>
      <span className="text-center text-[9px] font-semibold uppercase tracking-wider text-ink-subtle group-hover:text-ink">
        {brain.id}
      </span>
      {brain.executions_last_24h > 0 && (
        <span className="text-[9px] tabular-nums text-ink-muted">
          {brain.executions_last_24h}x
        </span>
      )}
    </Link>
  );
}

// ─── BrainLoopCard ────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  return `${Math.round(diff / 3_600_000)}h ago`;
}

function BrainLoopCard() {
  const { data, isLoading } = useQuery<BrainStatus>({
    queryKey: ['activity', 'brain-status'],
    queryFn: () => api.getBrainStatus(),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const { data: accData } = useQuery({
    queryKey: ['activity', 'brain-accuracy'],
    queryFn: () => api.getPredictionsAccuracy(),
    staleTime: 60_000,
  });

  return (
    <section
      aria-label="OMEGA Brain loop status"
      className="rounded-xl border border-border bg-surface p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <Brain size={14} className="text-ink-muted" aria-hidden />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          OMEGA Brain Loop
        </h2>
        {!isLoading && data && (
          <span
            className={cn(
              'ms-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
              data.running
                ? 'bg-success/10 text-success'
                : 'bg-warning/10 text-warning',
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                data.running ? 'animate-pulse bg-success' : 'bg-warning',
              )}
            />
            {data.running ? 'Running' : 'Paused'}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="h-8 w-full animate-pulse rounded bg-muted" />
      )}

      {!isLoading && data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg bg-canvas p-2.5">
            <p className="font-mono text-sm font-semibold text-ink">
              {data.snapshots_total.toLocaleString()}
            </p>
            <p className="text-[10px] text-ink-muted">Snapshots</p>
            <p className="mt-0.5 text-[10px] text-ink-subtle">
              last {relativeTime(data.last_snapshot_at)}
            </p>
          </div>
          <div className="rounded-lg bg-canvas p-2.5">
            <p className="font-mono text-sm font-semibold text-ink">
              {data.predictions_total.toLocaleString()}
            </p>
            <p className="text-[10px] text-ink-muted">Predictions</p>
            <p className="mt-0.5 text-[10px] text-ink-subtle">
              last {relativeTime(data.last_prediction_at)}
            </p>
          </div>
          <div className="rounded-lg bg-canvas p-2.5">
            <Link
              to="/omega/console"
              className="block hover:opacity-80"
            >
              <p className={cn(
                'font-mono text-sm font-semibold',
                data.pending_proposals_count > 0 ? 'text-amber-500' : 'text-ink',
              )}>
                {data.pending_proposals_count}
              </p>
              <p className="text-[10px] text-ink-muted">
                Pending proposal{data.pending_proposals_count !== 1 ? 's' : ''}
              </p>
              {data.pending_proposals_count > 0 && (
                <p className="mt-0.5 text-[10px] text-amber-500">→ Review</p>
              )}
            </Link>
          </div>
          <div className="rounded-lg bg-canvas p-2.5">
            <p className="font-mono text-sm font-semibold text-ink">
              {accData ? `${accData.accuracy_pct.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-ink-muted">Accuracy (7d)</p>
            <p className="mt-0.5 text-[10px] text-ink-subtle">
              {accData ? `${accData.total_outcomes} outcomes` : 'loading…'}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── LogStreamSection ─────────────────────────────────────────────

interface LogStreamSectionProps {
  entries: OmegaLogEntry[];
  total: number;
  paused: boolean;
  sseConnected: boolean;
  onPauseToggle: (paused: boolean) => void;
  onEntryClick: (entry: OmegaLogEntry) => void;
}

function LogStreamSection({
  entries,
  total,
  paused,
  sseConnected,
  onPauseToggle,
  onEntryClick,
}: LogStreamSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  // Auto-scroll to bottom on new entries unless user scrolled up
  useEffect(() => {
    const el = containerRef.current;
    if (!el || paused || !autoScrollRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [entries, paused]);

  // Detect manual scroll up → pause auto-scroll
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    autoScrollRef.current = atBottom;
  }, []);

  return (
    <section
      aria-label="Live log stream"
      className="flex flex-col rounded-xl border border-border bg-surface"
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Activity size={14} className="text-ink-muted" aria-hidden />
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Live Log Stream
        </h2>
        <span className="font-mono text-[10px] text-ink-muted">
          showing {entries.length.toLocaleString()} / {total.toLocaleString()}
        </span>
        {sseConnected && (
          <span className="flex items-center gap-1 text-[10px] font-medium text-success">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
            live
          </span>
        )}
        <button
          onClick={() => onPauseToggle(!paused)}
          className="ms-1 flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium text-ink-subtle hover:border-ink/40 hover:text-ink"
          aria-label={paused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
          data-testid="pause-scroll-btn"
        >
          {paused ? (
            <><Play size={10} aria-hidden /> Resume</>
          ) : (
            <><Pause size={10} aria-hidden /> Pause</>
          )}
        </button>
      </div>

      {/* Entry list */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex max-h-[520px] min-h-[120px] flex-col gap-1 overflow-y-auto p-3"
        data-testid="log-stream-container"
      >
        {entries.length === 0 ? (
          <div className="flex h-24 items-center justify-center text-xs text-ink-subtle">
            Waiting for log entries…
          </div>
        ) : (
          entries.map((entry) => (
            <LogEntryCard
              key={entry.seq}
              entry={entry}
              onClick={() => onEntryClick(entry)}
            />
          ))
        )}
      </div>
    </section>
  );
}

// ─── LogEntryCard ────────────────────────────────────────────────

const COLOR_CLASSES: Record<EntryColor, { bg: string; badge: string; dot: string }> = {
  blue: {
    bg: 'hover:bg-blue-500/5',
    badge: 'border-blue-400/40 bg-blue-500/10 text-blue-400',
    dot: 'bg-blue-400',
  },
  green: {
    bg: 'hover:bg-success/5',
    badge: 'border-success/40 bg-success/10 text-success',
    dot: 'bg-success',
  },
  orange: {
    bg: 'hover:bg-warning/5',
    badge: 'border-warning/40 bg-warning/10 text-warning',
    dot: 'bg-warning',
  },
  red: {
    bg: 'hover:bg-danger/5',
    badge: 'border-danger/40 bg-danger/10 text-danger',
    dot: 'bg-danger',
  },
  purple: {
    bg: 'hover:bg-purple-500/5',
    badge: 'border-purple-400/40 bg-purple-500/10 text-purple-400',
    dot: 'bg-purple-400',
  },
  teal: {
    bg: 'hover:bg-teal-400/5',
    badge: 'border-teal-400/40 bg-teal-500/10 text-teal-400',
    dot: 'bg-teal-400',
  },
  gray: {
    bg: 'hover:bg-ink-subtle/5',
    badge: 'border-border bg-canvas text-ink-subtle',
    dot: 'bg-ink-subtle',
  },
};

const LogEntryCard = ({
  entry,
  onClick,
}: {
  entry: OmegaLogEntry;
  onClick: () => void;
}) => {
  const color = getEntryColor(entry.entry_type);
  const cls = COLOR_CLASSES[color];
  const ts = extractTimestamp(entry);

  return (
    <button
      onClick={onClick}
      title={entry.entry_type}
      className={cn(
        'ltr-content flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start',
        'transition-colors',
        cls.bg,
      )}
      data-testid={`log-entry-${entry.seq}`}
    >
      {/* Seq */}
      <span className="w-10 shrink-0 font-mono text-[10px] tabular-nums text-ink-muted">
        #{entry.seq}
      </span>

      {/* Category dot — replaces the raw type badge (raw type on hover + drawer) */}
      <span
        className={cn('h-2 w-2 shrink-0 rounded-full', cls.dot)}
        data-testid={`entry-type-badge-${entry.seq}`}
        aria-hidden
      />

      {/* Human-readable sentence (default; raw type via hover/drawer) */}
      <span className="min-w-0 flex-1 truncate text-[11px] text-ink">
        {humanizeEntry(entry)}
      </span>

      {/* Timestamp */}
      {ts && (
        <span className="shrink-0 text-[10px] tabular-nums text-ink-muted">
          {relTime(ts)}
        </span>
      )}
    </button>
  );
};

/** Title-case a raw entry_type: underscores → spaces, capitalise words. */
function titleCaseType(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

/** "market_research" → "market research"; for inline use. */
function humanizeSlug(s: string): string {
  return s.replace(/[_-]+/g, ' ').trim();
}

/** Capitalise a brain id for a sentence; falls back to "OMEGA". */
function brainName(b: string | undefined): string {
  if (!b) return 'OMEGA';
  return b.charAt(0).toUpperCase() + b.slice(1);
}

/**
 * Humanizer (Founder dashboard): map an entry_type (+ payload) to a plain
 * English sentence. DEFAULT display — the raw type is still available on hover
 * (card `title`) and in the detail drawer. Unknown types fall back to a
 * title-cased form of the type so nothing ever shows as a raw snake_case token.
 */
function humanizeEntry(entry: OmegaLogEntry): string {
  const t = entry.entry_type;
  const p =
    entry.payload_json && typeof entry.payload_json === 'object'
      ? (entry.payload_json as Record<string, unknown>)
      : {};
  const str = (k: string): string | undefined =>
    typeof p[k] === 'string' ? (p[k] as string) : undefined;
  const brain = str('brain_id');
  const specialist = str('specialist_id');
  const question = str('question') ?? str('summary');

  switch (t) {
    case 'omega_genesis':
      return 'OMEGA was sparked (genesis)';
    case 'constitution_ratification':
      return 'Constitution ratified';
    case 'constitution_amendment':
      return 'Constitution amended';
    case 'foreseeability_envelope':
      return 'Foreseeability envelope published';
    case 'founder_key_fingerprint':
      return 'Founder key fingerprint recorded';
    case 'brain_binding_updated':
      return `${brainName(brain)} brain wiring updated`;
    case 'omega_kill':
      return 'Kill switch engaged';
    case 'omega_founder_kill':
      return 'Founder engaged the kill switch';
    case 'founder_sign_delegated':
      return 'Founder signature delegated';
    case 'market_intel_signal':
      return 'OMEGA analyzed market signals';
    case 'omega_brain_outcome':
      return `${brainName(brain)} completed an analysis`;
    case 'brain_execution':
      return specialist
        ? `${brainName(brain)} ran ${humanizeSlug(specialist)}`
        : `${brainName(brain)} brain ran a specialist`;
    case 'entity':
      return 'A signed entity was created';
    case 'attestation':
      return 'An external attestation was recorded';
    case 'consent':
      return 'Consent was granted';
    case 'revocation':
      return 'Consent was revoked';
    default:
      break;
  }

  if (t.startsWith('decision_')) {
    return question ? `OMEGA proposed a decision: ${question}` : 'OMEGA proposed a decision';
  }
  if (t.startsWith('trigger_')) {
    const slug = str('slug') ?? str('trigger_id');
    return slug ? `Trigger fired: ${humanizeSlug(slug)}` : 'A trigger fired';
  }
  if (t.startsWith('market_intel')) {
    return 'OMEGA analyzed market signals';
  }

  // Fallback: never show a raw snake_case token.
  const tail = question ?? specialist ?? str('kind') ?? str('slug');
  return tail ? `${titleCaseType(t)}: ${tail}` : titleCaseType(t);
}

// ─── DecisionsSidebar ─────────────────────────────────────────────

function DecisionsSidebar() {
  const { data, isLoading } = useQuery({
    queryKey: ['omega', 'decisions', 'pending'],
    queryFn: () =>
      apiFetch<{ decisions: PendingDecision[]; total: number }>('/omega/decisions/pending'),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const decisions = data?.decisions ?? [];

  return (
    <section
      aria-label="Recent decisions"
      className="rounded-xl border border-border bg-surface"
    >
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Recent Decisions
        </h2>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {isLoading ? (
          <div className="flex h-16 items-center justify-center text-xs text-ink-subtle">
            Loading…
          </div>
        ) : decisions.length === 0 ? (
          <div className="rounded-lg border border-border bg-canvas px-3 py-4 text-center text-xs text-ink-subtle">
            No pending decisions
          </div>
        ) : (
          decisions.map((d) => (
            <DecisionCard key={d.decision_id} decision={d} />
          ))
        )}
      </div>
    </section>
  );
}

function DecisionCard({ decision }: { decision: PendingDecision }) {
  const confidenceColor = {
    low: 'text-warning',
    medium: 'text-ink',
    high: 'text-success',
  }[decision.confidence] ?? 'text-ink';

  return (
    <div
      className="rounded-lg border border-border bg-canvas p-3"
      data-testid={`decision-card-${decision.decision_id}`}
    >
      <div className="mb-2 flex items-start gap-2">
        <span
          className={cn(
            'mt-0.5 shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase',
            decision.recommendation === 'approve' && 'border-success/40 bg-success/10 text-success',
            decision.recommendation === 'reject' && 'border-danger/40 bg-danger/10 text-danger',
            decision.recommendation === 'defer' && 'border-warning/40 bg-warning/10 text-warning',
          )}
        >
          {decision.recommendation}
        </span>
        <span className="text-[11px] font-medium leading-snug text-ink">
          {humanizeDecision(decision)}
        </span>
      </div>
      <div className="mb-2 flex items-center gap-2">
        <span className={cn('text-[10px] font-medium', confidenceColor)}>
          {decision.confidence} confidence
        </span>
        <span className="text-[10px] text-ink-muted">
          · expires {relTime(decision.expires_at)}
        </span>
      </div>
      {/* Action buttons — placeholder; full founder-sig flow is Phase 12 */}
      <div className="flex gap-1.5">
        {(['approve', 'defer', 'reject'] as const).map((action) => (
          <button
            key={action}
            disabled
            className="flex-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium capitalize text-ink-subtle opacity-50 cursor-not-allowed"
            title="Requires founder signature (Phase 12)"
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── EntryDrawer ──────────────────────────────────────────────────

function EntryDrawer({
  entry,
  onClose,
}: {
  entry: OmegaLogEntry;
  onClose: () => void;
}) {
  const color = getEntryColor(entry.entry_type);
  const cls = COLOR_CLASSES[color];
  const ts = extractTimestamp(entry);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 end-0 z-50 flex w-full max-w-lg flex-col overflow-y-auto border-s border-border bg-surface shadow-2xl"
        aria-label="Log entry detail"
        data-testid="entry-drawer"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'rounded border px-2 py-0.5 font-mono text-[10px] font-medium',
                cls.badge,
              )}
            >
              {entry.entry_type}
            </span>
            <span className="font-mono text-xs text-ink-muted">#{entry.seq}</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-ink-muted hover:bg-canvas hover:text-ink"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-5">
          {ts && (
            <div className="text-xs text-ink-muted">{new Date(ts).toLocaleString()}</div>
          )}

          <section>
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
              Payload
            </h3>
            <pre className="overflow-x-auto rounded-lg border border-border bg-canvas p-3 font-mono text-[11px] leading-relaxed text-ink">
              {JSON.stringify(entry.payload_json, null, 2)}
            </pre>
          </section>

          <section>
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
              Payload hash
            </h3>
            <code className="break-all font-mono text-[11px] text-ink-muted">
              {entry.payload_hash}
            </code>
          </section>

          <section>
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
              Engine signature
            </h3>
            <code className="break-all font-mono text-[11px] text-ink-muted">
              {entry.signature}
            </code>
          </section>
        </div>
      </aside>
    </>
  );
}
